import { inject, Injectable } from "@angular/core";
import { OrderResumeService } from "./order-resume.service";
import { of, tap } from "rxjs";
import { OrderRoutes, OrderRoutePaths } from "../../features/orders/orders-routes.enum";
import { LineDeviceType, PromotionConfigurationItem, SingleLineConfiguration } from "../models/line-configurations.model";
import { LicOrderSetup, LicInternetFlow, LicWirelessFlow, LicWirelessLine } from "../models/order/lic/lic.model";
import { StepStatusType } from "../models/order/order.model";
import { customerPreferences } from "../models/order/workflow/credit-check.model";
import { OrderSetupData } from "../models/order/workflow/order-setup.model";
import { CreditCheckStateService } from "../store/state/order/orderv2/workflows/credit-check/credit-check-state.service";
import { InternetPackageSelectionStateService } from "../store/state/order/orderv2/workflows/internet/internet-package-selection/internet-package-selection-state.service";
import { WirelessLineConfigurationStateService } from "../store/state/order/orderv2/workflows/wireless/wireless-line-configuration.state.service";
import { WirelessSetupStateService } from "../store/state/order/orderv2/workflows/wireless/wireless-setup.state.service";
import { WirelessSigfanStateService } from "../store/state/order/orderv2/workflows/wireless/wireless-sigfan.state.service";
import { WirelessFlowStateService } from "../store/state/order/orderv2/workflows/wireless/wireless-flow-state.service";
import { LicApiCallService } from "./lic-api-call.service";
import { WirelessProductPlusResponseService } from "../store/state/order/orderv2/order-api-response/wireless-pd-plus-response.service";
import { Line } from "../models/order/api-models/order-api-vi-auth-account-response.model";
import { OrderApiResponseStateService } from "../store/state/order/orderv2/order-api-response/order-api-response-state.service";
import { findOffers, isNextupPayoffOrPayup } from "../util/wireless-line-config-util";
import { NotificationService } from "./notification.service";
import { Message } from "../enums/message.enum";
import "../extensions/object.extension";

@Injectable({
    providedIn: 'root'
})
export class LicService extends OrderResumeService {

    internetPackageSelectionStateService = inject(InternetPackageSelectionStateService)
    wirelessSetupStateService = inject(WirelessSetupStateService)
    wirelessSigfanStateService = inject(WirelessSigfanStateService);
    creditCheckStateService = inject(CreditCheckStateService)

    licApiCallService = inject(LicApiCallService)
    wirelessLineConfigurationStateService = inject(WirelessLineConfigurationStateService)
    wirelessFlowStateService = inject(WirelessFlowStateService)
    wirelessPdPlusResponseService = inject(WirelessProductPlusResponseService)
    orderApiResponseStateService = inject(OrderApiResponseStateService)
    notificationService = inject(NotificationService)

    /** Long enough for the agent to read every affected line. */
    readonly quotedDeviceUnavailableToastLife = 20000

    constructor() {
        super();
    }

    processResponse() {
        const licResponse = this.licResponse();
        return of(licResponse).pipe(tap(() => {
            if (licResponse && licResponse.ApiResponses) {
                if (licResponse.ApiResponses.Login) {
                    this.processLogin(licResponse.ApiResponses.Login);
                }

                if (licResponse.ApiResponses.Welcome) {
                    this.processWelcome(licResponse.ApiResponses.Welcome);
                }
                if (licResponse.ApiResponses.PreOrder) {
                    this.processPreOrder(licResponse.ApiResponses.PreOrder);
                }

                if (licResponse.ApiResponses.StartOrder) {
                    this.processStartOrder(licResponse.ApiResponses.StartOrder);
                }

                if (licResponse.ApiResponses.OrderSetup) {
                    this.processOrderSetupByDefaults(licResponse.ApiResponses.OrderSetup);
                }

                if (licResponse.ApiResponses.Customer) {
                    this.processServiceInformationCollection(licResponse.ApiResponses.Customer);
                }
                if (licResponse.ApiResponses.InternetFlow) {
                    this.processInternetPackageSelection(licResponse.ApiResponses.InternetFlow);
                }
                if (licResponse.ApiResponses.WirelessFlow) {
                    this.processWirelessPackageSelection(licResponse.ApiResponses.WirelessFlow);
                }
            }
            return licResponse;
        }))
    }

    getLicLandingPage() {
        // const response = this.licResponse();
        // const startOrder = response?.ApiResponses?.StartOrder;
        // const campaigns = startOrder?.Campaigns ?? [];
        // if (response?.ApiResponses?.PreOrder?.OrderType === 'consumer' && campaigns.length > 0) {
        //     return campaigns.length > 1 ? OrderRoutes.ORDER_SETUP : OrderRoutes.SERVICE_INFORMATION;
        // }
        // if (campaigns.length > 1 || startOrder?.Accounts?.length > 1 ||
        //     startOrder?.Retailers?.length > 1 || startOrder?.SalesPersons?.length > 1) {
        //     return OrderRoutes.ORDER_SETUP;
        // }
        return this.getLandingPage();
    }

    getLandingPage() {
        const response = this.licResponse();
        const baseUrl = response?.MetaData?.LandingPage;
        return OrderRoutePaths[baseUrl] ? OrderRoutes[baseUrl] : OrderRoutes.WELCOME;
    }

    private processSigFan() {
        const response = this.licResponse();
        const sigFan = response?.ApiResponses?.OrderSetup?.SigFanDiscountType;
        this.wirelessSigfanStateService.updateData({
            formValue: {
                discountType: sigFan
            }
        });
    }

    private processCreditCheckForLineCount() {
        const response = this.licResponse();
        const wirelessFlow = response?.ApiResponses?.WirelessFlow;
        if (!wirelessFlow?.Lines || wirelessFlow?.Lines.length === 0) {
            return;
        }
        const newLicLines = (wirelessFlow.Lines ?? []).filter(
                    line => !line.Tn && line.TnType !== 'upgrade'
                );    
        const maxLineNumber = Math.max(newLicLines?.length, 0)
    
        this.creditCheckStateService.updateData({
            customerPreferences: {
                addLinesToExistingAccount: maxLineNumber ? 'yes': 'no',
                previousRequestedLines: maxLineNumber,
                requestedLines: maxLineNumber,
                portingNumber: !!wirelessFlow?.WirelessSetup?.IsPortingPhoneNumber
            } as customerPreferences,
        })
    }

    processOrderSetupByDefaults(orderSetup: LicOrderSetup) {
        const { PreOrder: preOrder, StartOrder: startOrder } = this.licResponse().ApiResponses;
        const salesPerson = startOrder?.SalesPersons?.find(person => person.UserId === this.userStateService.getUser()?.UserId)
            ?? startOrder?.SalesPersons?.[0];
        const orderSetupData: OrderSetupData = {
            orderOptions: {
                orderType: orderSetup.OrderType || preOrder?.OrderType,
                interactionType: orderSetup.InteractionType || preOrder?.InteractionType,
                location: orderSetup.LocationType || preOrder?.LocationType,
            },
            salesInformation: {
                retailer: orderSetup.RetailerName ?? '',
                store: orderSetup.RetailerName ? orderSetup.DealerCode : '',
                account: orderSetup.RetailerName ? '' : orderSetup.DealerCode,
                salesPerson: salesPerson?.UserId?.toString() ?? '',
                selectedDealerCode: orderSetup.DealerCode,
                campaign: orderSetup.CampaignId ?? '',
            },
            referenceNumbers: [],
            status: StepStatusType.InProgress
        }
        this.orderSetupStateService.updateData(orderSetupData);
        this.orderSetupStateService.markStepComplete();
    }

    processInternetPackageSelection(internetFlow: LicInternetFlow) {
        this.internetPackageSelectionStateService.updateData({ selectedPlanCode: internetFlow.InternetPackageSelection?.SelectedPlanCode });
        this.internetPackageSelectionStateService.markStepComplete();
    }

    processWirelessPackageSelection(wirelessFlow: LicWirelessFlow) {
        this.wirelessSetupStateService.updateData({ selectedCustomerCode: wirelessFlow.WirelessSetup?.SelectedCustomerCode });
        if (wirelessFlow.WirelessSetup?.SelectedCustomerCode === 'WRLS-COMP-EXG') {
          this.wirelessSetupStateService.updateData({ cpniConsentGrantedAndSubmitClicked: true });            
        }
        this.processSigFan()
        // this.processLineConfiguration(wirelessFlow)
        this.processCreditCheckForLineCount()

    }

    processLineConfiguration(wirelessFlow: LicWirelessFlow, existingLines: Line[]) {
        const state = wirelessFlow.Lines;
        const lines = this.buildLines(state, existingLines)
        this.wirelessLineConfigurationStateService.updateData(lines);
        // this.wirelessLineConfigurationStateService.markStepComplete();
    }

    buildLines(lines: LicWirelessLine[] = [], existingLines: Line[] = []) {
        if (existingLines?.length) {
            return this.buildExistingLines(existingLines, lines);
        }
        const newLines = lines?.filter(line => line && !line.Tn) ?? [];
        this.wirelessFlowStateService.updateData({ numberOfWirelessLinesInProgress: newLines.length });
        return newLines.reduce((lineConfigurations, line) => ({
            ...lineConfigurations,
            ['line' + line.LineNumber]: this.buildLine(line)
        }), {});
    }

    buildExistingLines(existingLines: Line[], licLines: LicWirelessLine[] = []) {
        let idx = 1;
        const lineState: Record<string, SingleLineConfiguration> = {};
        // Account lines keep their VI shape, which is looser than SingleLineConfiguration
        const sortedLicLines = (licLines ?? [])
            .filter(line => !!line)
            .sort((a, b) => Number(a.LineNumber) - Number(b.LineNumber));
        const matchedLicLines = new Set<LicWirelessLine>();

        existingLines.forEach(existingLine => {
            const matchingLine = this.findLicLineByTn(sortedLicLines, existingLine.Tn, matchedLicLines);
            if (matchingLine) {
                matchedLicLines.add(matchingLine);
            }
            const pendingUpgrade = !!matchingLine && isNextupPayoffOrPayup(existingLine);
            lineState['line' + idx] = matchingLine && !pendingUpgrade
                ? this.buildLine(matchingLine, true, idx)
                : this.mapExistingLineConfiguration(existingLine) as unknown as SingleLineConfiguration;
            if (pendingUpgrade) {
                lineState['line' + idx].lic = {
                    lineNumber: idx,
                    licLineNumber: Number(matchingLine.LineNumber),
                    pendingUpgrade: true
                };
            }
            idx++;
        });

        sortedLicLines
            .filter(line => !line.Tn && !matchedLicLines.has(line))
            .forEach(line => {
                lineState['line' + idx] = this.buildLine(line, false, idx);
                idx++;
            });
        this.wirelessFlowStateService.updateData({ numberOfWirelessLinesInProgress: idx - 1});

        return lineState;
    }

    findLicLineByTn(licLines: LicWirelessLine[], tn: string, excluded: Set<LicWirelessLine> = new Set()) {
        const normalizedTn = this.normalizeTn(tn);
        if (!normalizedTn) {
            return undefined;
        }
        return licLines.find(line => !excluded.has(line) && this.normalizeTn(line.Tn) === normalizedTn);
    }

    normalizeTn(tn: string) {
        return (tn ?? '').replace(/\D/g, '');
    }

    buildLine(line: LicWirelessLine, upgradeProcessInitiated = false, lineNumber = Number(line?.LineNumber)) {
        const tradeInPromotion = line?.Promotions?.find(promotion => promotion.PromotionType === 'TradeIn');
        let promoItem: PromotionConfigurationItem | null = null;
        const promotions: PromotionConfigurationItem[] = [];
        if (tradeInPromotion && tradeInPromotion.Metadata) {
            const tradeInDeviceKey = Object.keys(tradeInPromotion.Metadata).find(key => key.toLowerCase() === 'device');
            const tradeInValueKey = Object.keys(tradeInPromotion.Metadata).find(key => key.toLowerCase() === 'value');
            const tradeInDevice = tradeInPromotion.Metadata[tradeInDeviceKey];
            const tradeInValue = tradeInPromotion.Metadata[tradeInValueKey];

            promoItem = {
                tradeInDevice: tradeInDevice,
                tradeInValue: tradeInValue,
                promotionCode: tradeInPromotion.PromotionCode,
                type: 'premiumTradeIn',
                customizationCode: tradeInPromotion?.PromotionCode ?? '',
                isAccepted: true,
                lineNumber
            }

            promotions.push(promoItem);
        }

        const singleLineConfiguration = {
            device: {
                deviceType: line?.DeviceDetails?.NewByodOnhand ?? '',
                deviceIMEI: line?.DeviceDetails?.ByodIMEI ?? '',
                existingIMEI: line?.DeviceDetails?.DeviceIMEI ?? '',
                shippingOption: line?.DeviceDetails?.ShipType ?? '',
                deviceSIM: line?.DeviceDetails?.ByodSIM ?? '',
                selectedDevice: {
                    ChoiceCode: line?.DeviceDetails?.DeviceSku ?? '',
                    Name: line?.DeviceDetails?.DeviceName ?? '',
                    Upc: line?.DeviceDetails?.DeviceUpc ?? ''
                },
                upgradeProcessInitiated,
                ...(upgradeProcessInitiated && { transactionType: 'upgrade' })
            },
            protectionPlan: {
                planSelected: line?.InsurancePlanCode ?? '',
                planName: line?.InsurancePlanName ?? ''
            },
            wirelessPlan: {
                planSelected: line?.PlanChoiceCode ?? '',
                planName: line?.PlanChoiceName ?? '',
                disclosureChecked: true
            },
            installmentPlan: {
                planSelected: line?.PayPlanCode ?? '',
                planName: line?.PayPlanName ?? '',
                Usoc: line?.PayPlanCodeUsoc ?? ''
            },
            phoneNumber: {
                numberType: line?.TnType,
                pinNumber: line?.PortInfo?.Pin ?? '',
                portNumber: (line?.Tn ?? '').mobileFormat(),
                accountName: line?.PortInfo?.AccountName ?? '',
                accountNumber: line?.PortInfo?.AccountNumber ?? ''
            },
            promotions: promotions,
            lic: { lineNumber, licLineNumber: Number(line?.LineNumber) }
        } as SingleLineConfiguration
        return singleLineConfiguration
    }


    mapExistingLineConfiguration(line: Line) {
        return {
            device: {
                existingIMEI: line?.Device?.DeviceIMEI ?? '',
                transactionType: 'existing'
            },
            protectionPlan: {
                planSelected: line?.DataPlanInsuranceCode ?? '',
                planName: line?.DataPlanInsuranceName ?? ''
            },
            wirelessPlan: {
                planSelected:
                    line?.DataPlanChoiceCode ?? line?.DataPlanCode ?? '',
                planName: line?.DataPlanName ?? '',
                disclosureChecked: true
            },
            installmentPlan: {
                planSelected: line?.Device?.CurrentPayPlanCode ?? '',
                planName: '',
                Usoc: ''
            },
            phoneNumber: {
                transactionNumber: line?.Tn
                
            },
            promotions: []
        }
    } 

    processLicMappings() {
        const lineConfigurations = this.wirelessLineConfigurationStateService.state();
        const unavailableDevices: string[] = [];
        Object.keys(lineConfigurations ?? {}).forEach(lineKey => {
            const match = /^line(\d+)$/.exec(lineKey);
            if (match) {
                const unavailableDevice = this.clearUnavailableQuoteDevice(Number(match[1]));
                if (unavailableDevice) {
                    unavailableDevices.push(`Line ${match[1]}: ${unavailableDevice}`);
                }
                this.validateTradeInPromotion(Number(match[1]));
            }
        });
        if (unavailableDevices.length) {
            this.notificationService.showWarning(
                Message.WarningMessageQuotedDeviceUnavailable + unavailableDevices.join('; '),
                Message.WarningTitleQuotedDeviceUnavailable,
                undefined,
                this.quotedDeviceUnavailableToastLife
            );
        }
    }

    /**
     * A NEW device quoted by the partner but absent from the wireless PD catalog is cleared so the agent
     * selects another device. Returns the cleared device's name, or null when the line is unchanged.
     */
    clearUnavailableQuoteDevice(lineNumber: number) {
        const lineState = this.wirelessLineConfigurationStateService.getLineConfiguration(lineNumber);
        const device = lineState?.device;
        const sku = device?.selectedDevice?.ChoiceCode?.trim().toUpperCase();
        if (!sku || device?.deviceType?.toUpperCase() !== LineDeviceType.NEW) {
            return null;
        }

        const wirelessComponent = this.orderApiResponseStateService.wirelessProductDetailsData()?.Components?.Component
            ?.find(component => component?.ProductType?.toLowerCase() === 'wireless');
        const catalogDevices = findOffers(wirelessComponent)?.wirelessDevices ?? [];
        // Without a catalog the SKU cannot be judged; leave the quoted device in place
        if (catalogDevices.length === 0 || catalogDevices.some(choice => choice?.ChoiceCode?.trim().toUpperCase() === sku)) {
            return null;
        }

        this.wirelessLineConfigurationStateService.updateData({
            [`line${lineNumber}`]: { ...lineState, device: { ...device, selectedDevice: null } }
        });
        return device.selectedDevice?.Name || device.selectedDevice?.ChoiceCode;
    }

    validateTradeInPromotion(lineNumber: number) {
        const lineState = this.wirelessLineConfigurationStateService.getLineConfiguration(lineNumber);
        const tradeInPromotion = lineState?.promotions?.find(promotion => promotion.type === 'premiumTradeIn');
        if (!tradeInPromotion) {
            return false;
        }

        const tradeInCustomization = this.wirelessPdPlusResponseService.findTradeInPromotion(lineNumber)?.customization;
        const choices = tradeInCustomization?.Choices?.Choice ?? [];

        if (!tradeInCustomization) {
            return false;
        }

        const acceptChoice = choices.find(choice => choice.ChoiceCode?.endsWith('ACCEPT-SPC'));
        const declineChoice = choices.find(choice => choice.ChoiceCode?.endsWith('DECLINE-SPC'));
        if (!acceptChoice?.ChoiceCode || !declineChoice?.ChoiceCode) {
            return false;
        }

        const eligibleDevices = acceptChoice.PromotionTradeInEligibleDevices?.split('|').map(device => device.trim()).filter(Boolean) ?? [];

        const providerTradeInDevice = tradeInPromotion.tradeInDevice?.trim();
        const providerTradeInModel = providerTradeInDevice?.replace(/\s+\d+\s*(?:GB|TB)$/i, '');
        const tradeInDevice = eligibleDevices.find(device => device === providerTradeInDevice)
            ?? eligibleDevices.find(device => device === providerTradeInModel);

        const isAccepted = true;
        const selectedChoice = acceptChoice;
        const updatedPromotion: PromotionConfigurationItem = {
            ...tradeInPromotion,
            lineNumber,
            promotionCode: selectedChoice.ChoiceCode,
            customizationCode: selectedChoice.ChoiceSubGroupName ?? '',
            customizationType: tradeInCustomization.CustomizationType,
            isAccepted,
            name: tradeInCustomization.Name,
            acceptChoiceCode: acceptChoice.ChoiceCode,
            declineChoiceCode: declineChoice.ChoiceCode,
            description: selectedChoice.LongDescription ?? selectedChoice.Description ?? '',
            disclosure: selectedChoice.Disclosure ?? '',
            fee: selectedChoice.Fee ?? undefined,
            tradeInDevice: tradeInDevice ?? undefined,
            tradeInValue: acceptChoice.PromotionTradeInValue
        };

        this.wirelessLineConfigurationStateService.updateData({
            [`line${lineNumber}`]: {
                ...lineState,
                promotions: lineState.promotions?.map(promotion =>
                    promotion === tradeInPromotion ? updatedPromotion : promotion
                )
            }
        });

        return true;
    }

}